/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CampaignConfigSchema,
  ConfigComponent,
  ConfigSMSComponent,
  ConfigMMSComponent,
  MessageTemplate as MessageTemplateType,
  CampaignConfigGetResponse,
  CampaignConfigStatus,
} from '../types';
import { normalizeRecurringTemplateSettings } from './recurringTemplateHelpers';
import { normalizeTextToWinTemplateSettings } from './textToWinTemplateHelpers';
import { normalizeEnrollmentTemplateSettings } from './enrollmentTemplateHelpers';

export interface LoadedConfig {
  config: CampaignConfigSchema;
  status: CampaignConfigStatus;
}

/**
 * Convert a backend campaign config response into the editor's
 * CampaignConfigSchema shape. Pure: pass `nowIso` for a deterministic
 * `updatedAt` (the caller supplies `new Date().toISOString()`).
 *
 * Ported from the original CampaignConfigBuilder.tsx load effect (lines ~250-486),
 * with added hydration of the new recurring / text-to-win template settings.
 */
export const toSchema = (configData: CampaignConfigGetResponse, nowIso: string): LoadedConfig => {
  const backendConfig = configData.config;
  const components: ConfigComponent[] = [];

  // Convert message properties to individual components
  if (backendConfig.message) {
    if (backendConfig.message.types?.includes('sms')) {
      components.push({
        type: 'sms',
        name: 'sms-message',
        label: 'SMS Message',
        visible: backendConfig.message.text.visible,
        required: backendConfig.message.text.required,
        messageType: 'sms',
        messageText: backendConfig.message.text.value || '',
      } as ConfigSMSComponent);
    }

    if (backendConfig.message.types?.includes('mms')) {
      components.push({
        type: 'mms',
        name: 'mms-message',
        label: 'MMS Message',
        visible: backendConfig.message.text.visible,
        required: backendConfig.message.text.required,
        subject: backendConfig.message.subject.value || '',
        messageText: backendConfig.message.text.value || '',
      } as ConfigMMSComponent);
    }
  }

  // Add opt-out-message component
  components.push({
    type: 'text',
    name: 'opt-out-message',
    label: 'Opt-out Message',
    visible: backendConfig.opt_out_message?.visible,
    required: backendConfig.opt_out_message?.required,
    defaultText: backendConfig.opt_out_message?.value,
  });

  if (backendConfig.message) {
    components.push({
      type: 'checkbox',
      name: 'data-capture',
      label: 'Capture response to message as',
      visible: backendConfig.message.data_capture.visible,
      required: backendConfig.message.data_capture.required,
      defaultChecked: backendConfig.message.data_capture.active,
      dataCaptureType: 'contact_field',
    });
  }

  // Convert backend messageTemplates if they exist, else convert legacy message
  let messageTemplates: MessageTemplateType[] = [];
  if (
    (backendConfig as any).messageTemplates &&
    (backendConfig as any).messageTemplates.length > 0
  ) {
    messageTemplates = (backendConfig as any).messageTemplates;
  } else if (backendConfig.message) {
    const messageType =
      backendConfig.message.types.includes('mms') && backendConfig.message.types.includes('sms')
        ? 'sms-or-mms'
        : backendConfig.message.types.includes('mms')
        ? 'mms-only'
        : 'sms-only';

    messageTemplates = [
      {
        id: 'legacy-template',
        name: '',
        messageType,
        smsText: backendConfig.message.types.includes('sms')
          ? backendConfig.message.text.value || ''
          : '',
        mmsSubject: backendConfig.message.subject.value || '',
        mmsText: backendConfig.message.types.includes('mms')
          ? backendConfig.message.text.value || ''
          : '',
        dataCapture: backendConfig.message.data_capture.active
          ? {
              active: true,
              description: backendConfig.message.data_capture.description,
            }
          : undefined,
        mmsFieldsVisibility: {
          subject: true,
          text: true,
          attachment: true,
        },
        dataCaptureVisible: true,
        surveyVisible: false,
        scheduleVisible: true,
        oneMessageOnly: false,
      },
    ];
  }

  messageTemplates = messageTemplates.map(template => ({
    dataCaptureVisible: true,
    surveyVisible: false,
    scheduleVisible: true,
    oneMessageOnly: false,
    ...template,
  }));

  // Groups / keywords / appointment-type components
  if (backendConfig.recipients) {
    components.push({
      name: 'groups-field',
      label: 'Groups',
      visible: backendConfig.recipients.visible,
      required: backendConfig.recipients.required || false,
      type: 'select' as const,
      options: [],
      defaultValue: '',
      values: backendConfig.recipients.values || [],
    });
  }

  if (backendConfig.keywords) {
    components.push({
      name: 'keywords-field',
      label: 'Keywords',
      visible: backendConfig.keywords.visible,
      required: backendConfig.keywords.required || false,
      type: 'select' as const,
      options: [],
      defaultValue: '',
      values: backendConfig.keywords.values || [],
    });
  }

  if (backendConfig.appointment_type && backendConfig.appointment_type.visible) {
    components.push({
      type: 'select' as const,
      name: 'appointment-type',
      label: 'Calendar',
      visible: backendConfig.appointment_type.visible,
      required: backendConfig.appointment_type.required || false,
      options: [],
      defaultValue: '',
      values: backendConfig.appointment_type.value ? [backendConfig.appointment_type.value] : [],
    });
  }

  const config: CampaignConfigSchema = {
    icon: backendConfig.icon || 'Add',
    name: backendConfig.name || '',
    type: backendConfig.type,
    category: backendConfig.category,
    description: backendConfig.description || '',
    updatedAt: nowIso,
    applicationIds: Array.isArray((configData as any).applicationIds)
      ? (configData as any).applicationIds
      : typeof (configData as any).applicationIds === 'string'
      ? (configData as any).applicationIds
          .split(',')
          .map((id: string) => id.trim())
          .filter((id: string) => id)
      : [],
    message: backendConfig.message
      ? {
          ...backendConfig.message,
          text: {
            ...backendConfig.message.text,
            visible:
              backendConfig.message.types && backendConfig.message.types.length > 0
                ? backendConfig.message.text.visible
                : false,
          },
          subject: {
            ...backendConfig.message.subject,
            visible:
              backendConfig.message.types && backendConfig.message.types.length > 0
                ? backendConfig.message.subject.visible
                : false,
          },
        }
      : {
          text: { visible: false, required: true, value: null },
          types: [],
          subject: { visible: false, required: false, value: null },
          data_capture: {
            active: false,
            visible: false,
            dropdown: true,
            required: true,
            description: 'Capture response to message as',
            metadata_name: null,
          },
        },
    keywords: backendConfig.keywords,
    recipients: backendConfig.recipients,
    messages: backendConfig.messages,
    metadata: backendConfig.metadata,
    opt_out_message: {
      value: backendConfig.opt_out_message?.value,
      active: backendConfig.opt_out_message?.active,
      visible: backendConfig.opt_out_message?.visible,
      required: backendConfig.opt_out_message?.required,
    },
    schedule: {
      value: 'immediately',
      visible: false,
      required: false,
    },
    appointment_type: backendConfig.appointment_type,
    contact_date_type: backendConfig.contact_date_type || {
      value: null,
      visible: false,
      required: false,
      label: 'Contact Date Type',
    },
    attach_calendar_invite: backendConfig.attach_calendar_invite,
    components,
    messageTemplates,
    isPoll: backendConfig.isPoll,
  };

  // Hydrate type-specific template settings
  if (backendConfig.type === 'recurring') {
    config.recurringSettings = normalizeRecurringTemplateSettings(backendConfig.recurringSettings);
  } else if (backendConfig.recurringSettings) {
    config.recurringSettings = backendConfig.recurringSettings;
  }

  if (backendConfig.type === 'text_to_win') {
    config.textToWinSettings = normalizeTextToWinTemplateSettings(backendConfig.textToWinSettings);
  } else if (backendConfig.textToWinSettings) {
    config.textToWinSettings = backendConfig.textToWinSettings;
  }

  if (backendConfig.type === 'enrollment') {
    config.enrollmentSettings = normalizeEnrollmentTemplateSettings(backendConfig.enrollmentSettings);
  } else if (backendConfig.enrollmentSettings) {
    config.enrollmentSettings = backendConfig.enrollmentSettings;
  }

  return { config, status: configData.status };
};
